import { jsx as _jsx } from "react/jsx-runtime";
import { buildClassName } from '@bluebeam/build-class-name';
import { useDataAttributes } from '@bluebeam/react-hooks';
import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { MoveFocusInside } from 'react-focus-lock';
import { PanelContextProvider } from './PanelContextProvider';
import PanelScrollableContent from './PanelScrollableContent';
import PanelStickyFooter from './PanelStickyFooter';
import PanelStickyHeader from './PanelStickyHeader';
import { usePanelContext } from './usePanelContext';
/** A panel is a type of **molecule** that contain supplemental information to the main viewing area. */
const Panel = ({ children, className, initialFocusRef, placement = 'right', onDismiss, ...rest }) => {
    const { bodyId, panelId, titleId } = usePanelContext();
    const dataAttributes = useDataAttributes(rest);
    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        const triggerElement = document.activeElement;
        document.body.style.overflow = 'hidden';
        initialFocusRef?.current?.focus();
        return () => {
            document.body.style.overflow = originalOverflow;
            triggerElement?.focus();
        };
    }, [initialFocusRef]);
    return (_jsx(motion.aside, { animate: "visible", "aria-describedby": bodyId, "aria-labelledby": titleId, "aria-modal": true, className: buildClassName('elevation-1 fixed bottom-0 z-10 h-screen w-[448px] overflow-hidden bg-white', placement === 'right' ? 'right-0' : 'left-0', className), id: panelId, initial: "invisible", role: "dialog", onKeyDown: (event) => {
            if (event.key !== 'Escape') {
                return;
            }
            event.preventDefault();
            onDismiss();
        }, tabIndex: -1, transition: { damping: 60, stiffness: 500 }, variants: placement === 'right'
            ? { invisible: { left: '100vw', right: 'auto' }, visible: { left: 'auto', right: 0 } }
            : { invisible: { left: 'auto', right: '100vw' }, visible: { left: '0', right: 'auto' } }, ...dataAttributes, children: _jsx(MoveFocusInside, { className: "flex h-full flex-col", disabled: Boolean(initialFocusRef?.current), children: children }) }));
};
/** A panel is a type of **molecule** that contain supplemental information to the main viewing area. */
const PanelContainer = (props) => {
    return (_jsx(PanelContextProvider, { children: _jsx(Panel, { ...props }) }));
};
PanelContainer.ScrollableContent = PanelScrollableContent;
PanelContainer.StickyHeader = PanelStickyHeader;
PanelContainer.StickyFooter = PanelStickyFooter;
export default PanelContainer;
//# sourceMappingURL=Panel.js.map